// src/components/AIChatPanel.jsx
// Slide-over drawer for the AI Chat Assistant.
// 420px on desktop, full-width on mobile. Escape to close.

import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, MessageSquare, ChevronRight } from 'lucide-react';
import { useAgentChatContext } from '../context/AgentChatContext';
import useAgentChat from '../hooks/useAgentChat';
import { getSuggestedPrompts, getInputPlaceholder } from '../utils/agentChatHelpers';
import AIChatMessage from './AIChatMessage';
import AIChatInput from './AIChatInput';
import AIChatToolIndicator from './AIChatToolIndicator';
import AIAssistantAvatar from './AIAssistantAvatar';

// ─── Welcome / Suggested Prompts ────────────────────────────────────────────
// Shown only when the conversation has no messages yet. Click-to-send chips
// teach scope by example and reduce blank-page paralysis.

function WelcomeState({ pageContext, onSendPrompt, disabled }) {
  const prompts = getSuggestedPrompts(pageContext);
  return (
    <div className="flex-1 flex items-center justify-center text-center">
      <div className="px-2 w-full">
        <div className="flex justify-center mb-4">
          <AIAssistantAvatar size="lg" />
        </div>
        <p className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Hi! I'm your FlipperPro AI assistant.
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 mb-5 leading-relaxed max-w-[280px] mx-auto">
          I help with your deals — pipeline, comps, MAO, portfolio, and rehab estimates.
          I won't answer off-topic questions.
        </p>
        <div className="flex flex-col gap-2 max-w-[300px] mx-auto">
          {prompts.map((p) => (
            <button
              key={p}
              onClick={() => onSendPrompt(p)}
              disabled={disabled}
              className="group flex items-center justify-between gap-2 text-left text-[13px] text-slate-700 dark:text-slate-300
                bg-white dark:bg-slate-800/60 hover:bg-indigo-50 dark:hover:bg-indigo-500/10
                border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500/40
                rounded-xl px-3.5 py-2.5 transition-all
                shadow-sm hover:shadow-md hover:shadow-indigo-500/10
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex-1">{p}</span>
              <ChevronRight
                size={14}
                className="flex-shrink-0 text-slate-400 dark:text-slate-500 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Conversation List ──────────────────────────────────────────────────────

function ConversationList({ conversations, activeId, onSelect, onNew }) {
  if (!conversations.length) return null;

  return (
    <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 max-h-[140px] overflow-y-auto">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-500 font-semibold">
          Recent Conversations
        </span>
      </div>
      {conversations.slice(0, 5).map((c) => {
        const isActive = c.id === activeId;
        return (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            className={`w-full text-left flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs truncate transition-colors
              ${isActive ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
          >
            <span className="truncate mr-2">{c.title || 'New conversation'}</span>
            <span className="text-[10px] text-slate-500 dark:text-slate-600 flex-shrink-0">
              {formatRelative(c.lastMessageAt)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function formatRelative(timestamp) {
  if (!timestamp) return '';
  const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ─── Context Chip ───────────────────────────────────────────────────────────

function ContextChip({ context, onDismiss }) {
  if (!context || context.entityType === 'general') return null;

  const labels = {
    lead: '📍 Viewing lead',
    project: '🏗️ Viewing project',
    portfolio: '💰 Portfolio',
    dashboard: '📊 Dashboard',
  };

  return (
    <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
      <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 rounded-lg px-3 py-1.5 text-xs text-indigo-700 dark:text-indigo-400">
        <span>{labels[context.entityType] || '📄 Page'}</span>
        {context.entityId && (
          <span className="text-indigo-600/70 dark:text-indigo-300/70 truncate">ID: {context.entityId}</span>
        )}
        <button
          onClick={onDismiss}
          className="ml-auto text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          aria-label="Dismiss context"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Panel ─────────────────────────────────────────────────────────────

export default function AIChatPanel() {
  const {
    isOpen,
    closeChat,
    conversations,
    activeConversationId,
    selectConversation,
    startNewConversation,
    messages,
    isStreaming,
    currentTool,
    streamingMessage,
    pageContext,
    error,
  } = useAgentChatContext();
  const { sendMessage } = useAgentChat();

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showContext, setShowContext] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  // Auto-scroll to bottom only when user is near the bottom (within 150px)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingMessage, currentTool]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeChat();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeChat]);

  // Reset context visibility when context changes
  useEffect(() => {
    setShowContext(true);
  }, [pageContext.entityId]);

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm z-40 md:hidden"
        onClick={closeChat}
      />

      {/* Panel — frosted-glass elevated surface, sits over the page like a modal */}
      <div
        className="fixed top-0 right-0 h-full w-full md:w-[440px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl
          border-l border-slate-200 dark:border-slate-800
          z-50 flex flex-col shadow-2xl shadow-slate-900/10 dark:shadow-black/40
          animate-slideInRight"
        style={{ animationDuration: '250ms', animationFillMode: 'forwards' }}
        role="dialog"
        aria-label="AI Assistant"
      >
        {/* Header — soft ambient indigo wash behind the title bar adds depth without color slabbing */}
        <div className="relative flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none bg-gradient-to-b from-indigo-50/60 dark:from-indigo-950/30 to-transparent"
          />
          <div className="relative flex items-center gap-2.5">
            <AIAssistantAvatar size="md" />
            <div>
              <h3 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">AI Assistant</h3>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 dark:bg-emerald-400" />
                </span>
                Ready
              </div>
            </div>
          </div>
          <div className="relative flex items-center gap-1">
            <button
              onClick={startNewConversation}
              className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
              title="New conversation"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`p-1.5 rounded-md transition-colors
                ${showHistory ? 'bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200'}`}
              title="Conversation history"
            >
              <MessageSquare size={16} />
            </button>
            <button
              onClick={closeChat}
              className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Context chip */}
        {showContext && (
          <ContextChip context={pageContext} onDismiss={() => setShowContext(false)} />
        )}

        {/* Conversation list (toggleable) */}
        {showHistory && (
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={(id) => {
              selectConversation(id);
              setShowHistory(false);
            }}
            onNew={() => {
              startNewConversation();
              setShowHistory(false);
            }}
          />
        )}

        {/* Messages area */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
          {messages.length === 0 && !streamingMessage && !isStreaming && (
            <WelcomeState
              pageContext={pageContext}
              onSendPrompt={sendMessage}
              disabled={isStreaming}
            />
          )}

          {/* Persisted messages from Firestore.
              showAvatar: hide the assistant avatar on consecutive assistant messages
              so a multi-bubble answer reads as one grouped reply. */}
          {messages.map((msg, i) => {
            const prev = messages[i - 1];
            const showAvatar = msg.role !== 'assistant' || !prev || prev.role !== 'assistant';
            return (
              <AIChatMessage
                key={msg.id}
                role={msg.role}
                content={msg.content}
                timestamp={msg.createdAt}
                showAvatar={showAvatar}
              />
            );
          })}

          {/* Streaming tool indicator */}
          {currentTool && <AIChatToolIndicator tool={currentTool} done={false} />}

          {/* Streaming assistant message (accumulating).
              Hide avatar if the last persisted message was also assistant — same
              grouping rule as above. */}
          {streamingMessage && (
            <AIChatMessage
              role="assistant"
              content={streamingMessage}
              timestamp={null}
              showAvatar={messages[messages.length - 1]?.role !== 'assistant'}
            />
          )}

          {/* Error display */}
          {error && (
            <div className="px-3 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-xs text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <AIChatInput
          onSend={sendMessage}
          disabled={isStreaming}
          placeholder={getInputPlaceholder(pageContext)}
        />
      </div>

    </>
  );
}
